import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Terminal, Globe, Zap, CheckCircle, XCircle, Loader2, Star, MapPin, Mail, Phone } from 'lucide-react';

interface LogEntry {
  id: number;
  text: string;
  type: 'info' | 'success' | 'error' | 'warning' | 'system' | 'lead';
  timestamp: Date;
  leadData?: {
    name: string;
    rating?: number;
    reviewCount?: number;
    location?: string;
    hasEmail?: boolean;
    hasPhone?: boolean;
    imageUrl?: string;
  };
}

interface HackerLoadingScreenProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  mode: 'scraping' | 'generating';
  onComplete?: () => void;
  logs?: string[];
  stats?: {
    businessesFound?: number;
    businessesWithEmail?: number;
    businessesNoEmail?: number;
    citiesSearched?: number;
    progress?: number;
  };
  // Real-time data
  currentQuery?: string;
  currentLocation?: string;
  foundLeads?: Array<{
    name: string;
    rating?: number;
    reviewCount?: number;
    location?: string;
    hasEmail?: boolean;
    hasPhone?: boolean;
    imageUrl?: string;
  }>;
  // When true, render inline (no portal, no fixed fullscreen). Used by the
  // onboarding funnel's simulation to embed the same CLI inside a small
  // mock desktop-app window instead of taking over the whole viewport.
  inline?: boolean;
}

export function HackerLoadingScreen({
  isOpen,
  title,
  subtitle,
  mode,
  logs: externalLogs = [],
  stats = {},
  currentQuery,
  currentLocation,
  foundLeads = [],
  inline = false,
}: HackerLoadingScreenProps) {
  const [internalLogs, setInternalLogs] = useState<LogEntry[]>([]);
  const [showCursor, setShowCursor] = useState(true);
  const [initPhase, setInitPhase] = useState(0);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const logIdRef = useRef(0);
  const processedLeadsRef = useRef<Set<string>>(new Set());
  const externalLogsSeenRef = useRef(0);

  // Initial phases based on mode - brief startup messages. Memoized so the
  // array reference is stable across renders; otherwise the init-phase
  // useEffect below gets its deps invalidated on every parent re-render and
  // the 400ms timer never fires to advance the phase.
  const initMessages = useMemo(() => (
    mode === 'scraping'
      ? [
          { text: 'Initializing Romulus scraping engine...', type: 'info' as const },
          { text: 'Connecting to Google Places API...', type: 'info' as const },
          { text: 'Authentication successful', type: 'success' as const },
        ]
      : [
          { text: 'Initializing Romulus AI engine...', type: 'info' as const },
          { text: 'Loading Gemini AI model...', type: 'info' as const },
          { text: 'AI ready', type: 'success' as const },
        ]
  ), [mode]);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [internalLogs]);

  // Run initial phases on open
  useEffect(() => {
    if (!isOpen) return;

    if (initPhase < initMessages.length) {
      const timer = setTimeout(() => {
        setInternalLogs(prev => [...prev, {
          id: logIdRef.current++,
          text: initMessages[initPhase].text,
          type: initMessages[initPhase].type,
          timestamp: new Date(),
        }]);
        setInitPhase(prev => prev + 1);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initPhase, initMessages]);

  // Add query/location info after init
  useEffect(() => {
    if (initPhase >= initMessages.length && currentQuery && currentLocation) {
      const searchLog = `Searching for "${currentQuery}" in ${currentLocation}...`;
      const exists = internalLogs.some(l => l.text === searchLog);
      if (!exists) {
        setInternalLogs(prev => [...prev, {
          id: logIdRef.current++,
          text: searchLog,
          type: 'system',
          timestamp: new Date(),
        }]);
      }
    }
  }, [initPhase, currentQuery, currentLocation, initMessages.length, internalLogs]);

  // Add found leads as they come in - only after init phase completes
  useEffect(() => {
    // Wait for init phase to complete before showing leads
    if (initPhase < initMessages.length) return;

    foundLeads.forEach(lead => {
      if (!processedLeadsRef.current.has(lead.name)) {
        processedLeadsRef.current.add(lead.name);
        setInternalLogs(prev => [...prev, {
          id: logIdRef.current++,
          text: `Found: ${lead.name}`,
          type: 'lead',
          timestamp: new Date(),
          leadData: lead,
        }]);
      }
    });
  }, [foundLeads, initPhase, initMessages.length]);

  // Add external logs - only the NEW ones since last render. Without this,
  // every parent re-render appends the whole externalLogs array again,
  // duplicating lines.
  useEffect(() => {
    if (externalLogs.length > externalLogsSeenRef.current) {
      const newLines = externalLogs.slice(externalLogsSeenRef.current);
      externalLogsSeenRef.current = externalLogs.length;
      const newLogs = newLines.map(text => ({
        id: logIdRef.current++,
        text,
        type: text.includes('✓') ? 'success' as const :
              text.includes('✗') ? 'error' as const :
              text.includes('⚠') ? 'warning' as const : 'system' as const,
        timestamp: new Date(),
      }));
      setInternalLogs(prev => [...prev, ...newLogs]);
    }
  }, [externalLogs]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setInternalLogs([]);
      setInitPhase(0);
      logIdRef.current = 0;
      processedLeadsRef.current = new Set();
      externalLogsSeenRef.current = 0;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-3 h-3 text-primary-400" />;
      case 'error': return <XCircle className="w-3 h-3 text-red-400" />;
      case 'warning': return <Zap className="w-3 h-3 text-accent-400" />;
      case 'system': return <Terminal className="w-3 h-3 text-primary-300" />;
      case 'lead': return <Globe className="w-3 h-3 text-primary-400" />;
      default: return <div className="w-3 h-3 rounded-full bg-primary-400 animate-pulse" />;
    }
  };

  const getLogColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'success': return 'text-primary-400';
      case 'error': return 'text-red-400';
      case 'warning': return 'text-accent-400';
      case 'system': return 'text-primary-300';
      case 'lead': return 'text-primary-400';
      default: return 'text-gray-300';
    }
  };

  const modalContent = (
    <div className={`${inline ? 'relative w-full h-full' : 'fixed inset-0 z-[9999]'} bg-black flex flex-col overflow-hidden font-mono`}>
      {/* Animated background grid - dusty rose */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(91, 58, 58, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(91, 58, 58, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite',
        }} />
      </div>

      {/* Scanline effect */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute inset-0" style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(91, 58, 58, 0.03) 2px, rgba(91, 58, 58, 0.03) 4px)',
        }} />
      </div>

      {/* Header */}
      <div className="relative z-10 p-6 border-b border-primary-500/20">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-xl border-2 border-white bg-black flex items-center justify-center overflow-hidden">
              <img src="/wolfromulus.png" alt="Romulus" className="w-16 h-16 object-contain" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full animate-ping" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-wider font-heading">
              {title}
            </h1>
            {subtitle && (
              <p className="text-white/60 text-sm">{subtitle}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Loader2 className="w-5 h-5 text-primary-400 animate-spin" />
            <span className="text-primary-400 text-sm">PROCESSING...</span>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {mode === 'scraping' && (
        <div className="relative z-10 px-6 py-3 bg-primary-500/5 border-b border-primary-500/20 flex gap-8 flex-wrap">
          <div className="text-sm">
            <span className="text-primary-500/60">QUERY:</span>
            <span className="text-primary-400 ml-2">{currentQuery || 'Loading...'}</span>
          </div>
          <div className="text-sm">
            <span className="text-primary-500/60">LOCATION:</span>
            <span className="text-primary-400 ml-2">{currentLocation || 'Loading...'}</span>
          </div>
          <div className="text-sm">
            <span className="text-primary-500/60">FOUND:</span>
            <span className="text-primary-400 ml-2">{stats.businessesFound || 0}</span>
          </div>
          <div className="text-sm">
            <span className="text-primary-500/60">WITH CONTACT:</span>
            <span className="text-primary-300 ml-2">{stats.businessesWithEmail || 0}</span>
          </div>
        </div>
      )}

      {/* Main terminal area */}
      <div className="relative z-10 flex-1 overflow-hidden p-6">
        <div className="h-full bg-black/50 rounded-lg border border-primary-500/30 overflow-hidden flex flex-col">
          {/* Terminal header */}
          <div className="px-4 py-2 bg-primary-500/10 border-b border-primary-500/20 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-accent-500/80" />
            <div className="w-3 h-3 rounded-full bg-primary-500/80" />
            <span className="ml-4 text-primary-500/60 text-xs">
              romulus@terminal ~ {mode === 'scraping' ? 'scrape' : 'generate'}
            </span>
          </div>

          {/* Terminal content */}
          <div
            ref={logContainerRef}
            className="flex-1 overflow-y-auto p-4 text-sm"
          >
            {internalLogs.map((log) => (
              <div key={log.id} className="mb-2 animate-fadeIn">
                {log.type === 'lead' && log.leadData ? (
                  // Rich lead card display
                  <div className="flex items-start gap-3 p-3 bg-primary-500/5 border border-primary-500/20 rounded-lg mb-2">
                    {log.leadData.imageUrl ? (
                      <img
                        src={log.leadData.imageUrl}
                        alt={log.leadData.name}
                        className="w-12 h-12 rounded-lg object-cover border border-primary-500/30"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary-500/20 flex items-center justify-center border border-primary-500/30">
                        <Globe className="w-6 h-6 text-primary-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-primary-400 font-medium truncate">{log.leadData.name}</span>
                        {log.leadData.rating && (
                          <span className="flex items-center gap-1 text-accent-400 text-xs">
                            <Star className="w-3 h-3 fill-current" />
                            {log.leadData.rating}
                            {log.leadData.reviewCount && (
                              <span className="text-gray-500">({log.leadData.reviewCount})</span>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {log.leadData.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {log.leadData.location}
                          </span>
                        )}
                        {log.leadData.hasEmail && (
                          <span className="flex items-center gap-1 text-primary-400">
                            <Mail className="w-3 h-3" />
                            Email
                          </span>
                        )}
                        {log.leadData.hasPhone && (
                          <span className="flex items-center gap-1 text-accent-400">
                            <Phone className="w-3 h-3" />
                            Phone
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-primary-500/40 text-xs">
                      {log.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                ) : (
                  // Regular log line
                  <div className="flex items-center gap-2">
                    <span className="text-primary-500/40 text-xs min-w-[80px] shrink-0">
                      [{log.timestamp.toLocaleTimeString()}]
                    </span>
                    <span className="shrink-0">{getLogIcon(log.type)}</span>
                    <span className={getLogColor(log.type)}>{log.text}</span>
                  </div>
                )}
              </div>
            ))}

            {/* Waiting cursor */}
            {initPhase >= initMessages.length && (
              <div className="flex items-center gap-2 mb-1">
                <span className="text-primary-500/40 text-xs min-w-[80px] shrink-0">
                  [{new Date().toLocaleTimeString()}]
                </span>
                <Loader2 className="w-3 h-3 text-primary-400 animate-spin shrink-0" />
                <span className="text-primary-400">
                  {mode === 'scraping' ? 'Scanning for businesses...' : 'Generating content...'}
                  <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity`}>▊</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {stats.progress !== undefined && (
        <div className="relative z-10 px-6 pb-6">
          <div className="h-2 bg-primary-500/10 rounded-full overflow-hidden border border-primary-500/30">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-primary-400 transition-all duration-300"
              style={{ width: `${stats.progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-primary-500/60 text-xs">Progress</span>
            <span className="text-primary-400 text-xs">{stats.progress}%</span>
          </div>
        </div>
      )}

      {/* Keyboard hint */}
      <div className="relative z-10 px-6 pb-4 text-center">
        <p className="text-primary-500/40 text-xs">
          Please wait while Romulus processes your request...
        </p>
      </div>

      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );

  // Inline mode embeds the screen inside a small desktop-app window. The
  // original CLI chrome was designed for fullscreen (text-2xl title, big
  // padding, full-size lead cards). To avoid a "cram every huge element into
  // a tiny window" look we scale the entire rendered UI down via transform
  // and oversize the inner box by 1/scale so it still fills the frame.
  if (inline) {
    const scale = 0.62;
    return (
      <div className="absolute inset-0 overflow-hidden">
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: `${100 / scale}%`,
            height: `${100 / scale}%`,
          }}
        >
          {modalContent}
        </div>
      </div>
    );
  }
  return createPortal(modalContent, document.body);
}

export default HackerLoadingScreen;
